import { Link, useParams } from "react-router-dom";

import { AgentTasksIndicator } from "./AgentTasksIndicator";
import { BuildStatusPill } from "./BuildStatusPill";
import { ChatTab } from "./ChatTab";
import { DiffTab } from "./DiffTab";
import { PreviewControls } from "./PreviewControls";
import { PullRequestControls } from "./PullRequestControls";
import { RetryStart } from "./RetryStart";
import { ServicesTab } from "./ServicesTab";
import styles from "./TaskView.module.css";
import { TerminalsTab } from "./TerminalsTab";
import { useDocumentTitle } from "../../chrome/useDocumentTitle";
import { useFaviconAttention } from "../../chrome/useFaviconAttention";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { type LiveDiffFrame, useTaskDiff } from "../../data/api/diffs";
import { archiveTask, useTask } from "../../data/api/tasks";
import { useTaskTerminals } from "../../data/api/terminals";
import { useRealtimeScope } from "../../data/realtime/useRealtimeScope";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";
import { waitingOnAgentsLabel } from "../../lib/waitingOnAgents";
import { paths } from "../../routing/paths";
import { useNav } from "../../routing/useNav";

const TAB_KEYS = ["chat", "diff", "terminals", "services"] as const;
type TabKey = (typeof TAB_KEYS)[number];
const isTabKey = (value: string | undefined): value is TabKey =>
  (TAB_KEYS as readonly string[]).includes(value ?? "");

const TERMINAL = new Set(["cancelled", "done", "failed"]);
const CLOSED_TO_MESSAGES = new Set(["cancelled", "failed"]);

export const TaskView = () => {
  // The route's `:taskId` segment is the *label* — `T-441` — which is unique
  // only within a workspace, so it is bound as `taskLabel` and never used as an
  // id. Resolving it needs the repository, which the same URL carries; `taskId`
  // below is the ULID that comes back, and everything keys on that.
  const { repoId = "", tab: tabParam, taskId: taskLabel = "" } = useParams();
  const nav = useNav();
  const activeTab: TabKey = isTabKey(tabParam) ? tabParam : "chat";
  const { error, loading, refetch, task } = useTask(
    repoId || null,
    taskLabel || null,
  );
  const taskId = task?.id ?? null;
  // A browser tab per task is how people work here, so it is named for the
  // task itself — the label stands in until the title loads, and a rename
  // renames the browser tab with it.
  useDocumentTitle(task?.title ?? taskLabel);
  // …and badged when this task stops working while the user is on some other
  // tab. `useTask` refetches on `task.updated`, so a task that stops — asks a
  // question, ends a turn, finishes, fails — is caught as it happens rather
  // than on the next visit.
  useFaviconAttention(task?.status, task?.lastActivityAt);

  // Live open-terminal count for the tab badge (terminals.md §Surface).
  const { refetch: refetchTerminals, terminals } = useTaskTerminals(taskId);
  useRealtimeScope(taskId ? `task:${taskId}` : null, (event) => {
    if (
      event.kind === "terminal.opened" ||
      event.kind === "terminal.exited" ||
      event.kind === "resync"
    ) {
      refetchTerminals();
    }
  });
  const liveTerminals = terminals.filter(
    (t) => t.status === "open" || t.status === "provisioning",
  ).length;

  // The diff's size, on the tab, whichever tab the reader is on. This is most
  // of what "following along" means for somebody watching the chat: the work
  // growing, without having to sit on the Diff tab to see it
  // (diffs-and-pull-requests.md §Diff sources).
  const { applyFrame, diff, refetch: refetchDiff } = useTaskDiff(taskId);
  useRealtimeScope(taskId ? `task:${taskId}` : null, (event) => {
    if (event.kind === "diff.snapshot.created" || event.kind === "resync") {
      refetchDiff();
    } else if (event.kind === "diff.live.updated") {
      applyFrame(event.summary as LiveDiffFrame);
    }
  });

  if (loading && !task) {
    return <div className={styles.loading}>Loading task…</div>;
  }
  if (error || !task) {
    return (
      <EmptyState
        body="This task doesn't exist, or the link is stale."
        title="Task not found."
      />
    );
  }

  // A cancelled or failed task is as finished as a done one, so it files away
  // with the same quiet Archive rather than the confirm-first one.
  const isTerminal = TERMINAL.has(task.rawStatus);
  // Closed to new messages is not the same as terminal. A `done` task keeps
  // its sandbox until it is archived precisely so the user can take another
  // turn on it, so the composer stays live until then; `cancelled` and
  // `failed` have had theirs torn down and stay closed. Matches the server's
  // gate in routes/chat.ts.
  const canSend = !task.archived && !CLOSED_TO_MESSAGES.has(task.rawStatus);
  // Archiving from the task page has nothing left to show here once it's
  // done — send the user back to the board they came from.
  const onArchive = (): void => {
    void archiveTask(task.id)
      .then(() => nav.repo(repoId))
      .catch(() => refetch());
  };
  // Archiving a live agent shuts it down first (server-side), so it warrants a
  // confirm; archiving a finished one is just filing and does not.
  const onArchiveLive = (): void => {
    if (
      !confirm(
        `Archive ${task.displayId}? The agent is shut down and its sandbox torn down.`,
      )
    ) {
      return;
    }
    void archiveTask(task.id)
      .then(() => nav.repo(repoId))
      .catch(() => refetch());
  };

  const tabs: ReadonlyArray<{
    readonly icon: React.ReactNode;
    readonly key: TabKey;
    readonly label: string;
  }> = [
    { icon: <Icon.Chat size={14} />, key: "chat", label: "Chat" },
    { icon: <Icon.GitDiff size={14} />, key: "diff", label: "Diff" },
    { icon: <Icon.Terminal size={14} />, key: "terminals", label: "Terminals" },
    { icon: <Icon.Server size={14} />, key: "services", label: "Services" },
  ];

  return (
    <div className={styles.taskPage}>
      {/* The task's id, title and status are the breadcrumb's; what is left
          here is what the top bar has no room for. Every part of it can be
          absent — a task running nothing, with no checks and no branch yet —
          and the bar goes with them. So it can appear and disappear mid-turn,
          as a subagent starts and finishes: accepted, because it only happens
          before the agent has branched, and the branch holds the bar open for
          the rest of the task. */}
      <div className={styles.taskHead}>
        {task.waitingOnAgents > 0 ? (
          <span className={styles.taskWaiting}>
            {waitingOnAgentsLabel(task.waitingOnAgents)}
          </span>
        ) : null}
        <AgentTasksIndicator taskId={task.id} />
        <BuildStatusPill taskId={task.id} />
        {task.branch ? (
          <span className={styles.taskBranch}>
            <Icon.Branch size={11} />
            {task.branch}
          </span>
        ) : null}
      </div>
      <div className={cx(styles.tabbar, "u-hide-scrollbar")} role="tablist">
        {/* Each tab is its own URL, so each tab is a link — ⌘-click opens the
            diff or the terminals of this task in a second tab. */}
        {tabs.map((t) => (
          <Link
            aria-selected={activeTab === t.key}
            className={cx(styles.tab, activeTab === t.key && styles.active)}
            key={t.key}
            role="tab"
            to={paths.task(repoId, taskLabel, t.key)}
          >
            {t.icon} {t.label}
            {t.key === "terminals" && liveTerminals > 0 ? (
              <span className={styles.tabCount}>{liveTerminals}</span>
            ) : null}
            {t.key === "diff" && diff && diff.files.length > 0 ? (
              <span
                className={cx(styles.tabDelta, !diff.sealed && styles.tabLive)}
                title={
                  diff.sealed
                    ? undefined
                    : "Tinsmith is still writing — this is updating live"
                }
              >
                +{diff.linesAdded} −{diff.linesRemoved}
              </span>
            ) : null}
          </Link>
        ))}
        <div className={styles.grow} />
        <PreviewControls
          onSandboxLost={refetch}
          previewsDisabled={task.previewsDisabled}
          provisioning={task.provisioning}
          sandboxReleased={task.sandboxReleased}
          taskId={task.id}
        />
        <PullRequestControls taskId={task.id} taskStatus={task.rawStatus} />
        {task.canRetryStart ? (
          <RetryStart onRetried={refetch} taskId={task.id} />
        ) : null}
        {/* One button, one meaning — "I am done with this". On a live agent it
            shuts the agent down and tears the sandbox down before archiving,
            which is why it confirms first; on a finished one it just files it
            away. Both are called Archive because the distinction is ours, not
            the user's. An archived task gets no button: archiving is one-way,
            so there is nothing left to offer. */}
        {task.archived ? null : isTerminal ? (
          <Button className={styles.rightAction} onClick={onArchive} size="sm">
            <Icon.Archive size={12} /> Archive
          </Button>
        ) : (
          <Button
            className={styles.rightAction}
            onClick={onArchiveLive}
            size="sm"
            variant="danger"
          >
            <Icon.Archive size={12} /> Archive
          </Button>
        )}
      </div>
      <div className={styles.body}>
        {activeTab === "chat" ? (
          <ChatTab
            canSend={canSend}
            repoId={repoId}
            taskId={task.id}
            taskStatus={task.rawStatus}
            workspaceId={task.workspaceId}
          />
        ) : activeTab === "diff" ? (
          <DiffTab taskId={task.id} />
        ) : activeTab === "services" ? (
          <ServicesTab
            onSandboxLost={refetch}
            sandboxReleased={task.sandboxReleased}
            taskId={task.id}
          />
        ) : activeTab === "terminals" ? (
          <TerminalsTab
            onSandboxLost={refetch}
            sandboxReleased={task.sandboxReleased}
            taskId={task.id}
          />
        ) : (
          <TabPlaceholder
            label={tabs.find((t) => t.key === activeTab)?.label ?? "This tab"}
          />
        )}
      </div>
    </div>
  );
};

const TabPlaceholder = ({ label }: { readonly label: string }) => (
  <EmptyState
    body={`The ${label} tab isn't wired to the live server yet — it's coming in a future iteration.`}
    title={`${label} — coming soon`}
  />
);
